/**
 * AccountSelector Component
 * Searchable dropdown/list for selecting from all existing accounts
 * Used when account suggestions are empty or have low confidence
 *
 * WP-DOC-20: Created to allow manual account selection as fallback
 */
import { useState, useMemo, useCallback } from 'react';
import { cn } from '@/utils/cn';
import type { Account } from '@/types';

/**
 * Extended account type that may include connection info
 * This matches AccountWithConnection from services/accounts.ts
 */
interface AccountWithOptionalConnection extends Account {
  connection?: {
    institutionName?: string;
    status?: string;
    lastSyncAt?: string;
  };
}

export interface AccountSelectorProps {
  accounts: AccountWithOptionalConnection[];
  selectedAccountId: string | null;
  onSelect: (accountId: string) => void;
  placeholder?: string;
}

/**
 * Format account type for display
 */
function formatAccountType(type: string): string {
  const typeMap: Record<string, string> = {
    checking: 'Checking',
    savings: 'Savings',
    credit_card: 'Credit Card',
    credit: 'Credit Card',
    investment: 'Investment',
    loan: 'Loan',
    mortgage: 'Mortgage',
    depository: 'Bank Account',
    brokerage: 'Brokerage',
  };
  return typeMap[type?.toLowerCase() ?? ''] ?? (type?.charAt(0)?.toUpperCase() ?? '') + (type?.slice(1) ?? '');
}

export function AccountSelector({
  accounts,
  selectedAccountId,
  onSelect,
  placeholder = 'Search accounts...',
}: AccountSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  // Filter accounts based on search query
  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) {
      return accounts;
    }

    const query = searchQuery.toLowerCase();
    return accounts.filter((account) => {
      // Search by name
      if (account.name?.toLowerCase()?.includes(query)) return true;
      // Search by institution (from connection or parsed from name)
      if (account.connection?.institutionName?.toLowerCase()?.includes(query)) return true;
      // Search by mask
      if (account.mask?.toLowerCase()?.includes(query)) return true;
      // Search by type
      if (account.type?.toLowerCase()?.includes(query)) return true;
      if (account.subtype?.toLowerCase()?.includes(query)) return true;
      return false;
    });
  }, [accounts, searchQuery]);

  // Get the selected account for display
  const selectedAccount = useMemo(() => {
    if (!selectedAccountId) return null;
    return accounts.find((a) => a.id === selectedAccountId) ?? null;
  }, [accounts, selectedAccountId]);

  // Handle account selection
  const handleSelect = useCallback(
    (accountId: string) => {
      onSelect(accountId);
      setIsExpanded(false);
      setSearchQuery('');
    },
    [onSelect]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsExpanded(false);
      setSearchQuery('');
    }
  }, []);

  if (accounts.length === 0) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
        <p className="text-sm text-gray-500">No existing accounts found.</p>
        <p className="text-sm text-gray-500 mt-1">Create a new account to continue.</p>
      </div>
    );
  }

  return (
    <div className="relative" onKeyDown={handleKeyDown}>
      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!isExpanded) setIsExpanded(true);
          }}
          onFocus={() => setIsExpanded(true)}
          placeholder={selectedAccount ? selectedAccount.name : placeholder}
          className={cn(
            'w-full px-4 py-2 pr-10 border rounded-lg text-sm',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            selectedAccount ? 'border-primary-300 bg-primary-50' : 'border-gray-300'
          )}
          aria-label="Search accounts"
          aria-expanded={isExpanded}
          aria-controls="account-selector-list"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <SearchIcon className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Selected account indicator */}
      {selectedAccount && !isExpanded && (
        <div className="mt-2 p-3 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckIcon className="w-4 h-4 text-primary-600 flex-shrink-0" />
              <span className="font-medium text-primary-700">{selectedAccount.name}</span>
              {selectedAccount.mask && (
                <span className="text-sm text-primary-600">****{selectedAccount.mask}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="text-xs text-primary-600 hover:text-primary-700 underline"
            >
              Change
            </button>
          </div>
        </div>
      )}

      {/* Dropdown list */}
      {isExpanded && (
        <div
          id="account-selector-list"
          className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg"
          role="listbox"
        >
          {filteredAccounts.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              No accounts match your search.
            </div>
          ) : (
            <ul className="py-1">
              {filteredAccounts.map((account) => (
                <li key={account.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(account.id)}
                    className={cn(
                      'w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                      selectedAccountId === account.id && 'bg-primary-50'
                    )}
                    role="option"
                    aria-selected={selectedAccountId === account.id}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              'font-medium truncate',
                              selectedAccountId === account.id
                                ? 'text-primary-700'
                                : 'text-gray-900'
                            )}
                          >
                            {account.name}
                          </span>
                          {account.mask && (
                            <span className="text-sm text-gray-500 flex-shrink-0">
                              ****{account.mask}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-500">
                            {formatAccountType(account.type)}
                          </span>
                          {account.connection?.institutionName && (
                            <>
                              <span className="text-xs text-gray-400">|</span>
                              <span className="text-xs text-gray-500">
                                {account.connection.institutionName}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {selectedAccountId === account.id && (
                        <CheckIcon className="w-4 h-4 text-primary-600 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Backdrop to close dropdown */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setIsExpanded(false);
            setSearchQuery('');
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

export default AccountSelector;
